import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { ConfirmModal } from '../ui/Modal';
import organizationsService from '../../services/organizationsService';

// ── Teams ────────────────────────────────────────────────────────────────────

export function TeamsTab({ org, canEdit, myTeams }) {
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState('');
  const [confirmDetach, setConfirmDetach] = useState(null);

  const load = useCallback(async () => {
    try {
      setTeams(await organizationsService.getTeams(org.id));
    } catch (e) {
      setError(e.message);
    }
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Only teams the user OWNS and that are not already attached: attaching
  // needs both sides to consent, and the server refuses anything else — so
  // offering a team you merely belong to would be offering a guaranteed error.
  const attachable = useMemo(() => {
    const attachedIds = new Set(teams.map((t) => String(t.id)));
    return (myTeams || []).filter(
      (t) => !attachedIds.has(String(t.id)) && !t.organization_id && t.role === 'owner'
    );
  }, [teams, myTeams]);

  const attach = async (teamId) => {
    setError('');
    try {
      await organizationsService.attachTeam(org.id, teamId);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not attach that team.');
    }
  };

  const detach = async (teamId) => {
    setError('');
    try {
      await organizationsService.detachTeam(org.id, teamId);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not detach that team.');
    }
  };

  return (
    <div className="org-list">
      {error && <p className="org-error">{error}</p>}

      {teams.length === 0 && (
        <p className="org-hint">
          No teams yet. Attaching one lets this organization's admins administer it —
          and, once billing is set up, pay for it.
        </p>
      )}

      {teams.map((t) => (
        <div key={t.id} className="org-row">
          <Users size={16} className="org-row-icon" aria-hidden />
          <div className="org-row-texts">
            <span className="org-row-name">{t.name}</span>
          </div>
          {canEdit && (
            <button type="button" className="org-btn" onClick={() => setConfirmDetach(t)}>
              Detach
            </button>
          )}
        </div>
      ))}

      {canEdit && attachable.length > 0 && (
        <div className="org-attach">
          <span className="org-field-label">Attach a team you own</span>
          {attachable.map((t) => (
            <button key={t.id} type="button" className="org-btn" onClick={() => attach(t.id)}>
              <Plus size={13} /> {t.name}
            </button>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDetach}
        onClose={() => setConfirmDetach(null)}
        onConfirm={() => {
          const t = confirmDetach;
          setConfirmDetach(null);
          if (t) detach(t.id);
        }}
        title="Detach this team?"
        message={
          confirmDetach
            ? `${confirmDetach.name} keeps everything — its members, workspaces, vault and boards. It returns to being administered and paid for by its own owner.`
            : ''
        }
        confirmText="Detach"
        cancelText="Cancel"
      />
    </div>
  );
}
